// Library methods
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import SnackbarMessage from "src/components/shared/alerts/SnackbarMessage/SnackbarMessage";
import JobCard from "./JobCard/JobCard";
import { ChevronLeftIcon, FileIcon } from "src/assets/icons";

// Hooks
import { useEMRHubById } from "src/services/emr/hooks/emr-hubs/useEMRHubById";
import { useEMRJobs } from "src/services/emr/hooks/emr-jobs/useEMRJobs";
import useEMRLiveUpdates from "src/services/emr/live/useEMRLiveUpdates";

// Styles
import {
  getSurfaceTokens,
  ModalCustomSurfaceLink,
  PageContainerWrapper,
  getEmrPageBackground,
} from "src/styles/mui/muiStylesHelper";
import { useTheme, Theme } from "@mui/material/styles";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// TODO: remove this dummy data
// const emrJobs = [
//   {
//     jobId: "f12cd221-2bc4-4350-ad41-0afa7f7af62a",
//     title: "hfa-sample-zz-B-2026-06-15",
//     status: "Complete",
//     revision: 1,
//     completionMode: "DeclaredCount",
//     declaredFileCount: 1,
//     quietSeconds: null,
//     connectionId: "01a0d13d-2c68-7b2d-b5e0-5d776fa39017",
//     clinicId: "3e9d5c1c-36cd-4e88-8621-0559d0ad6c8a",
//     createdAt: "2026-09-24T02:28:07.4525664+00:00",
//     lastFileAt: "2026-09-24T02:28:18.1092635+00:00",
//     completedAt: "2026-09-24T02:28:18.1092635+00:00",
//     fileCount: 1,
//     steps: [
//       {
//         step: "Received",
//         state: null,
//         detail: null,
//         updatedAt: null,
//       },
//       {
//         step: "TypeIdentified",
//         state: null,
//         detail: null,
//         updatedAt: null,
//       },
//       {
//         step: "DataExtracted",
//         state: null,
//         detail: null,
//         updatedAt: null,
//       },
//       {
//         step: "Crops",
//         state: null,
//         detail: null,
//         updatedAt: null,
//       },
//       {
//         step: "AddedToRetinaLogik",
//         state: null,
//         detail: null,
//         updatedAt: null,
//       },
//       {
//         step: "Patient",
//         state: null,
//         detail: null,
//         updatedAt: null,
//       },
//       {
//         step: "PatientExamLink",
//         state: null,
//         detail: null,
//         updatedAt: null,
//       },
//     ],
//   },
// ];

const ACCENT_TEAL = "#1B8F76";

// Below this width the Status/Files/Created/Completed columns (which never
// shrink) would crush the flexible Title column down to nothing, so the
// header + rows scroll horizontally together instead of overlapping.
const JOB_TABLE_MIN_WIDTH = "700px";

const getCardBackground = (theme: Theme) =>
  theme.palette.mode === "dark" ? "#00000090" : "rgba(255, 255, 255, 0.55)";

const EmrJobs = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { hubId } = useParams();
  const { onSurface } = getSurfaceTokens(theme);
  const { clinicInfo, isLoading } = useClinicContext();

  useEMRLiveUpdates(clinicInfo?.id);
  const { snackbarInfo, setSnackbarInfo } = useSnackbarContext();

  const { data: emrHub } = useEMRHubById(clinicInfo?.id, hubId);
  const { data: emrJobs, isLoading: isLoadingJobs } = useEMRJobs(
    clinicInfo?.id,
    hubId
  );

  const canUseEMR = useMemo(
    () => clinicInfo?.licensing?.canUseEMR && !isLoading,
    [clinicInfo?.licensing?.canUseEMR, isLoading]
  );

  const isClinicReady = Boolean(clinicInfo?.id) && !isLoading;

  useEffect(() => {
    if (isClinicReady && !canUseEMR) {
      navigate("/dashboard");
    }
  }, [navigate, canUseEMR, isClinicReady]);

  return (
    <Box
      sx={{
        ...PageContainerWrapper(theme),
        background: getEmrPageBackground(theme),
        color: getSurfaceTokens(theme).onSurface,
        padding: "0px",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          maxHeight: "fit-content",
          gap: "16px",
          padding: "16px",
        }}
      >
        <Box display="flex" width="100%" flexDirection="column" gap="16px">
          <Box display="flex" justifyContent="flex-start">
            <Button
              startIcon={<ChevronLeftIcon />}
              sx={ModalCustomSurfaceLink(theme)}
              onClick={() => navigate("/data-hub")}
            >
              {t("Back to Data Hub")}
            </Button>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap="8px"
          >
            <Typography
              fontSize="16px"
              fontWeight={600}
              textOverflow="ellipsis"
              overflow="hidden"
              whiteSpace="nowrap"
              maxWidth="100%"
            >
              {emrHub?.alias}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              backgroundColor: getCardBackground(theme),
              borderRadius: "8px",
              padding: "16px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box display="flex" alignItems="center" gap="8px">
                <FileIcon style={{ color: ACCENT_TEAL }} />
                <Typography
                  fontSize="16px"
                  fontWeight={600}
                  color={ACCENT_TEAL}
                >
                  {t("Jobs")}
                </Typography>
              </Box>
            </Box>

            <Box display="flex" flexWrap="wrap" gap="12px">
              {isLoadingJobs && (
                <Typography fontSize="14px" color={onSurface}>
                  {t("Loading...")}
                </Typography>
              )}
              {!isLoadingJobs && !emrJobs?.length && (
                <Typography fontSize="14px" color={onSurface}>
                  {t("No jobs found.")}
                </Typography>
              )}
              {emrJobs && emrJobs.length > 0 && (
                <Box sx={{ width: "100%", overflowX: "auto" }}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap="8px"
                    sx={{ minWidth: JOB_TABLE_MIN_WIDTH }}
                    mb="8px"
                  >
                    <Box display="flex" alignItems="center" gap="16px" px="8px">
                      <Typography
                        fontSize="12px"
                        color={onSurface}
                        sx={{ flexShrink: 0, minWidth: "90px" }}
                      >
                        {t("Status")}
                      </Typography>
                      <Typography
                        fontSize="12px"
                        color={onSurface}
                        sx={{ flex: 1, minWidth: "150px" }}
                      >
                        {t("Title")}
                      </Typography>
                      <Typography
                        fontSize="12px"
                        color={onSurface}
                        sx={{ flexShrink: 0, minWidth: "80px" }}
                      >
                        {t("Files")}
                      </Typography>
                      <Typography
                        fontSize="12px"
                        color={onSurface}
                        sx={{ flexShrink: 0, minWidth: "130px" }}
                      >
                        {t("Created")}
                      </Typography>
                      <Typography
                        fontSize="12px"
                        color={onSurface}
                        sx={{ flexShrink: 0, minWidth: "130px" }}
                      >
                        {t("Completed")}
                      </Typography>
                    </Box>

                    <Grid container spacing="8px">
                      {emrJobs?.map((job) => (
                        <Grid item key={job.jobId} xs={12}>
                          <JobCard job={job} />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Snackbar success and error messages */}
      <SnackbarMessage
        snackbarInfo={snackbarInfo}
        setSnackbarInfo={setSnackbarInfo}
      />
    </Box>
  );
};

export default EmrJobs;
